import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';

import { AuthService } from '../../auth/auth.service';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { useDispatcher, UseStore } from '../../store.decorator';
import { LandingComm, SearchItem, SwitchboardService } from '../core';
import { Column, SortOrder } from '../grid';
import { useSelector } from '../store-use-selector';

import { createTableData } from './function/create-grid-data.function';
import { createReloadActionPayload } from './function/create-reload-action-payload.function';
import { createSearchParams } from './function/create-search-params.function';
import { TranslationKeyMap } from './landing.i18n';
import { landingReducer } from './landing.reducer';
import { LandingState } from './landing.state';
import { ColumnId } from './model/column-id.model';
import { GridDays } from './model/grid-days.model';
import { GridOptions } from './model/grid-options.model';
import { GridType } from './model/grid-type.model';
import { TableData } from './model/table-data.model';
import { TeamAlt } from './model/team-alt.model';
import { changeDateRange } from './state/change-date-range.action';
import { filterRows } from './state/filter-rows.action';
import { reload } from './state/reload.action';
import { resetGrid } from './state/reset-grid.action';
import { sortRows } from './state/sort-rows.action';
import { toggleGridShowAll } from './state/toggle-grid-show-all.action';

@Component({
    selector: 'cmps-eval-landing',
    templateUrl: './landing.component.html',
    host: {
        class: 'cmps-eval-landing',
    },
    viewProviders: [SwitchboardService],
})
@TakeUntilDestroy()
@UseStore<LandingState>('evaluation.landing', landingReducer)
export class LandingComponent
    implements OnInit, StateToProps<LandingState>, LandingComm
{
    state: LandingState;

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _GridType = GridType;

    /** @private */
    _GridDays = GridDays;

    /** @private */
    _TeamAlt = TeamAlt;

    /** @private */
    _ColumnId = ColumnId;

    /** @private */
    _SortOrder = SortOrder;

    /** @private */
    _forMeTableData: TableData;

    /** @private */
    _byMeTableData: TableData;

    /** @private */
    _forMyTeamTableData: TableData;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _router: Router,
        private readonly _authService: AuthService,
        private readonly _switchboardService: SwitchboardService,
    ) {}

    ngOnInit(): void {
        this._switchboardService?.sbLandingConnect(this);

        useSelector<SearchItem[]>('evaluation.landing.forMe.rows', () => null)
            .pipe(
                tap((state) => {
                    this._forMeTableData = createTableData(
                        state,
                        this.state.forMe.sortOptions,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector<SearchItem[]>('evaluation.landing.byMe.rows', () => null)
            .pipe(
                tap((state) => {
                    this._byMeTableData = createTableData(
                        state,
                        this.state.byMe.sortOptions,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useSelector<SearchItem[]>(
            'evaluation.landing.forMyTeam.rows',
            () => null,
        )
            .pipe(
                tap((state) => {
                    this._forMyTeamTableData = createTableData(
                        state,
                        this.state.forMyTeam.sortOptions,
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        useDispatcher(
            this,
            reload(
                createReloadActionPayload({
                    userId: this._authService.user.id,
                }),
            ),
        );
    }

    sbLandingDidUpdate(draftId?: string, responseId?: number): void {
        const grids: GridOptions[] = [
            this.state.forMe,
            this.state.byMe,
            this.state.forMyTeam,
        ];
        grids.forEach((g) => {
            const found = g?.rows?.some((r) => {
                if (draftId != null) {
                    return r.draftId === draftId;
                }
                if (responseId != null) {
                    return r.responseId === responseId;
                }
                return false;
            });
            if (!found) {
                return;
            }

            useDispatcher(
                this,
                changeDateRange({
                    type: g.type,
                    days: g.days,
                    params: createSearchParams({
                        type: g.type,
                        days: g.days,
                        userId: this._authService.user.id,
                        teamAlt:
                            g === GridType.FOR_MY_TEAM
                                ? this.state.forMyTeam.teamAlt
                                : null,
                    }),
                }),
            );
        });
    }

    /** @private */
    _onCreateNewEvalClick(): void {
        void this._router.navigate(['evaluation']);
    }

    /** @private */
    _onSearchEvalsClick(): void {
        void this._router.navigate(['evaluation-search']);
    }

    /** @private */
    _onGridDateRangeChange(grid: GridType, value: GridDays): void {
        if (
            (grid === GridType.FOR_ME && value === this.state.forMe.days) ||
            (grid === GridType.BY_ME && value === this.state.byMe.days) ||
            (grid === GridType.FOR_MY_TEAM &&
                value === this.state.forMyTeam.days)
        ) {
            return;
        }

        useDispatcher(
            this,
            changeDateRange({
                type: grid,
                days: value,
                params: createSearchParams({
                    type: grid,
                    days: value,
                    userId: this._authService.user.id,
                    teamAlt:
                        grid === GridType.FOR_MY_TEAM
                            ? this.state.forMyTeam.teamAlt
                            : null,
                }),
            }),
        );
    }

    /** @private */
    _onResetClick(grid: GridType): void {
        useDispatcher(this, resetGrid({ grid }));
    }

    /** @private */
    _onTeamGridAltChange(value: TeamAlt): void {
        if (value === this.state.forMyTeam.teamAlt) {
            return;
        }

        useDispatcher(
            this,
            changeDateRange({
                type: this.state.forMyTeam.type,
                days: this.state.forMyTeam.days,
                params: createSearchParams({
                    type: this.state.forMyTeam.type,
                    days: this.state.forMyTeam.days,
                    userId: this._authService.user.id,
                    teamAlt: value,
                }),
                teamAlt: value,
            }),
        );
    }

    /** @private */
    _onGridToggleShowAllClick(grid: GridType): void {
        useDispatcher(
            this,
            toggleGridShowAll({
                type: grid,
            }),
        );
    }

    /** @private */
    _onColumnSortClick(
        grid: GridType,
        c: Column,
        sortOrder: SortOrder = SortOrder.ASC,
    ): void {
        this._ngZone.run(() => {
            useDispatcher(
                this,
                sortRows({
                    grid,
                    options: { columnId: c.id, order: sortOrder },
                }),
            );
        });
    }

    /** @private */
    _onTextFilterChange(
        ctx: any /* intentional any */,
        o: GridOptions,
        value: any,
        overlay: any /* intentional any */,
    ): void {
        this._ngZone.run(() => {
            ctx?.filterFilterOpts(value, o.selectedFilterOptions);
            // TODO FIXME overlay is being blocked for change detection
            overlay?._hdl.ref?.detectChanges();
        });
    }

    /** @private */
    _onTextFilterItemClick(
        ctx: any /* intentional any */,
        grid: GridType,
        c: Column,
        value: any,
    ): void {
        this._ngZone.run(() => {
            useDispatcher(
                this,
                filterRows({ grid, columnId: c.id as ColumnId, value }),
            );
            // must pull after useDispatcher() above to get state change
            let g: GridOptions;
            if (grid === GridType.FOR_ME) {
                g = this.state.forMe;
            } else if (grid === GridType.BY_ME) {
                g = this.state.byMe;
            } else if (grid === GridType.FOR_MY_TEAM) {
                g = this.state.forMyTeam;
            }
            ctx?.toggleFilterFilterOpts(g.selectedFilterOptions);
        });
    }

    /** @private */
    _onGridViewRowClick(item: SearchItem): void {
        this._ngZone.run(() => {
            this._onGridSelectRowClick(item, true);
        });
    }

    /** @private */
    _onGridEditRowClick(item: SearchItem): void {
        this._ngZone.run(() => {
            this._onGridSelectRowClick(item, false);
        });
    }

    private _onGridSelectRowClick(item: SearchItem, viewOnly: boolean): void {
        let cmds: any[] = null;
        if (item.draftId != null) {
            cmds = ['evaluation-landing', 'drafts', item.draftId];
        }
        if (item.responseId != null) {
            cmds = ['evaluation-landing', 'responses', item.responseId];
        }
        if (cmds == null) {
            return;
        }
        void this._router.navigate(cmds, {
            queryParams: { viewOnly },
            queryParamsHandling: 'merge',
        });
    }
}
